import Component from '@glimmer/component';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { registerDestructor } from '@ember/destroyable';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import Icon from '../components/icon';
import pullToRefresh from '../modifiers/pull-to-refresh.ts';
import HeartIcon from '~icons/lucide/heart';
import VideoIcon from '~icons/lucide/video';
import UploadIcon from '~icons/lucide/upload';
import SearchIcon from '~icons/lucide/search';
import ShieldIcon from '~icons/lucide/shield';
import TagIcon from '~icons/lucide/tag';
import CompassIcon from '~icons/lucide/compass';
import ListVideoIcon from '~icons/lucide/list-video';
import AlertTriangleIcon from '~icons/lucide/alert-triangle';
import RefreshCwIcon from '~icons/lucide/refresh-cw';
import type Session from '../services/session';
import type NetworkStatus from '../services/network-status';
import type IndexRoute from '../routes/index.ts';
import SyncStatusCard from '../components/sync-status-card';
import SourceCard from '../components/source-card';
import ItemCard from '../components/item-card';
import { type ItemResource } from '../components/item-card/utils.gts';
import type { UserItem } from '../schemas/user-item.ts';

function toItemResource(userItem: UserItem): ItemResource {
  const item = userItem.item as Record<string, unknown> | null;
  if (!item) {
    return { id: userItem.id } as ItemResource;
  }
  return {
    id: item['id'] as string,
    ...item,
  } as unknown as ItemResource;
}

interface Signature {
  Args: {
    model: Awaited<ReturnType<IndexRoute['model']>>;
  };
}

export default class IndexPage extends Component<Signature> {
  @service declare session: Session;
  @service declare router: RouterService;
  @service declare networkStatus: NetworkStatus;

  constructor(owner: Owner, args: Signature['Args']) {
    super(owner, args);
    const unsubscribe = this.networkStatus.onReconnect(() => {
      if (this.args.model.loadError) {
        void this.router.refresh();
      }
    });
    registerDestructor(this, unsubscribe);
  }

  @tracked searchQuery = '';

  @action updateSearchQuery(event: Event) {
    this.searchQuery = (event.target as HTMLInputElement).value;
  }

  @action async refresh() {
    await this.router.refresh();
  }

  @action submitSearch(event: Event) {
    event.preventDefault();
    if (this.searchQuery.trim()) {
      void this.router.transitionTo('search', {
        queryParams: { q: this.searchQuery.trim(), page: '1' },
      });
    }
  }

  get userName(): string {
    const user = this.session.user;
    return user?.name ?? user?.email ?? 'there';
  }

  <template>
    <div {{pullToRefresh this.refresh}} class="mx-auto max-w-3xl">
      <div class="hero mb-8">
        <div class="hero-content text-center">
          <div class="max-w-2xl">
            <h1 class="text-4xl font-bold">
              Welcome back,
              {{this.userName}}
            </h1>
            <p class="text-base-content/70 py-4 text-lg">
              Extract mentions from videos, build your collection, and discover
              new content.
            </p>

            {{! Search input }}
            <form
              class="join mx-auto w-full max-w-md"
              {{on "submit" this.submitSearch}}
            >
              <input
                type="text"
                aria-label="Search"
                placeholder="Search movies, shows, books…"
                class="input join-item input-bordered w-full"
                value={{this.searchQuery}}
                {{on "input" this.updateSearchQuery}}
              />
              <button type="submit" class="btn btn-primary join-item">
                <Icon @icon={{SearchIcon}} class="h-5 w-5" />
              </button>
            </form>
          </div>
        </div>
      </div>

      <SyncStatusCard />

      {{! Stats cards }}
      {{#if @model.loadError}}
        <div class="alert alert-error mb-8 flex items-center justify-between">
          <div class="flex items-center gap-2">
            <Icon @icon={{AlertTriangleIcon}} class="h-5 w-5 shrink-0" />
            <span>Couldn't load your stats.</span>
          </div>
          <button
            type="button"
            class="btn btn-sm btn-ghost gap-1"
            {{on "click" this.refresh}}
          >
            <Icon @icon={{RefreshCwIcon}} class="h-4 w-4" />
            Retry
          </button>
        </div>
      {{else}}
        <div class="mb-8 grid grid-cols-2 gap-4">
          <LinkTo
            @route="items"
            class="bg-base-100 stat rounded-box cursor-pointer shadow transition-shadow hover:shadow-lg"
          >
            <div class="stat-figure text-primary">
              <Icon @icon={{HeartIcon}} class="h-8 w-8" />
            </div>
            <div class="stat-title">My Items</div>
            <div class="stat-value text-primary text-2xl">
              {{@model.itemCount}}
            </div>
          </LinkTo>

          <LinkTo
            @route="sources"
            class="bg-base-100 stat rounded-box cursor-pointer shadow transition-shadow hover:shadow-lg"
          >
            <div class="stat-figure text-secondary">
              <Icon @icon={{VideoIcon}} class="h-8 w-8" />
            </div>
            <div class="stat-title">My Sources</div>
            <div class="stat-value text-secondary text-2xl">
              {{@model.sourceCount}}
            </div>
          </LinkTo>
        </div>
      {{/if}}

      {{! Recent items }}
      {{#if @model.recentItems.length}}
        <div class="mb-8">
          <div class="mb-3 flex items-center justify-between">
            <h2 class="text-xl font-bold">Recent Items</h2>
            <LinkTo @route="items" class="btn btn-ghost btn-sm gap-1">
              View all
            </LinkTo>
          </div>
          <div class="grid grid-cols-1 gap-3">
            {{#each @model.recentItems as |userItem|}}
              <ItemCard @item={{toItemResource userItem}} />
            {{/each}}
          </div>
        </div>
      {{/if}}

      {{! Recent sources }}
      {{#if @model.recentSources.length}}
        <div class="mb-8">
          <div class="mb-3 flex items-center justify-between">
            <h2 class="text-xl font-bold">Recent Sources</h2>
            <LinkTo @route="sources" class="btn btn-ghost btn-sm gap-1">
              View all
            </LinkTo>
          </div>
          <div class="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {{#each @model.recentSources as |userSource|}}
              {{#if userSource.contentSource}}
                <SourceCard
                  @source={{userSource.contentSource}}
                  @hideTitle={{true}}
                />
              {{/if}}
            {{/each}}
          </div>
        </div>
      {{/if}}

      {{! Quick actions }}
      <div class="bg-base-100 rounded-box p-6 shadow">
        <h2 class="mb-4 text-xl font-bold">Quick Actions</h2>
        <div class="flex flex-wrap gap-3">
          <LinkTo @route="extract" class="btn btn-primary gap-2">
            <Icon @icon={{UploadIcon}} class="h-5 w-5" />
            Extract URL
          </LinkTo>
          <LinkTo @route="search" class="btn btn-outline gap-2">
            <Icon @icon={{SearchIcon}} class="h-5 w-5" />
            Search
          </LinkTo>
          <LinkTo @route="explore" class="btn btn-outline gap-2">
            <Icon @icon={{CompassIcon}} class="h-5 w-5" />
            Explore
          </LinkTo>
          <LinkTo @route="sources" class="btn btn-outline gap-2">
            <Icon @icon={{ListVideoIcon}} class="h-5 w-5" />
            Sources
          </LinkTo>
          <LinkTo @route="collections" class="btn btn-outline gap-2">
            <Icon @icon={{TagIcon}} class="h-5 w-5" />
            Collections
          </LinkTo>
          {{#if this.session.isModerator}}
            <a href="/admin/" class="btn btn-outline gap-2">
              <Icon @icon={{ShieldIcon}} class="h-5 w-5" />
              Admin
            </a>
          {{/if}}
        </div>
      </div>
    </div>
  </template>
}
